/** @jsxImportSource @emotion/react */
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {IAppState} from '../../../../core/redux/IAppState';
import {sortByExpirationDateAsc} from '../../module';
import {getErroredEntitlementShares} from './module';
import {getLoading} from '../../../../core/redux/api/module';
import {ACTION_TYPE} from '../../../../core/redux/actionType';
import {getFnFEntitlementsAction} from './getFnFEntitlementsAction';
import {clearRedeemedEntitlementAction} from '../clearRedeemedEntitlementAction';
import {getEntitlementSharesAction} from '../../getEntitlementSharesAction';
import {useCallback, useEffect, useMemo, useState} from 'react';
import ModalView, {ModalContent} from '../../../../layout/modal/ModalView';
import {css} from '@emotion/react';
import {COLUMN_SPACING} from '../../../../forms/styles';
import ModalTabHeader, {Tab} from '../../../../layout/modal/ModalTabHeader';
import fnf from '../../../../assets/icons/fnf.svg';
import calendar from '../../../../assets/icons/calendar.svg';
import { doNothing } from '../../../../language/module';
import Switcher from '../../../../layout/Switcher';
import FnFHistoryTab from './FnFHistoryTab';
import { getAllRedemptionHistory } from '../module';
import {HistoricalRedemption} from '../historicalRedemption';
import { getResource } from '../../../../language/resources/getResource';
import { FnFRedeemTab } from './FnFRedeemTab';

const FnFModal = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  ${COLUMN_SPACING};
`;

type Props = {
  guestId: string;
  onDismiss: () => void;
}
const enum TABS {
  REDEEM,
  SHARE_HISTORY,
}

/**
 * Modal for redeeming F&F entitlements
 * @param guestId
 * @param onDismiss
 */
export const FnFEntitlementModal = (props: Props ) => {

  const guest = useSelector((state: IAppState) => state.guests.get(props.guestId));

  const entitlements = useMemo(() => sortByExpirationDateAsc((guest.friendsAndFamily || [])), [guest.friendsAndFamily]);
  const isLoadingEntitlements = useSelector((state: IAppState) => !!getLoading(state, props.guestId, ACTION_TYPE.GET_FNF_ENTITLEMENTS_REQUEST));
  const isLoadingEntitlementShares = useSelector((state: IAppState) => !!getLoading(state, props.guestId, ACTION_TYPE.ENTITLEMENT_SHARES_REQUEST));
  const entitlementSharesError = useSelector((state: IAppState) => getErroredEntitlementShares(state, props.guestId), shallowEqual);
  const isRedeeming = useSelector((state: IAppState) => !!getLoading(state, props.guestId, ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST));
  const entitlementShares = useSelector((state: IAppState) => state.entitlementShares.filter(share => share.guestId === props.guestId), shallowEqual);
  const resources = useSelector((state: IAppState) => state.locale);
  const dispatch = useDispatch();
  const getFnFEntitlements = useCallback(() => dispatch(getFnFEntitlementsAction(props.guestId)), [dispatch, props.guestId]);
  const clearRedemption = useCallback(() => dispatch(clearRedeemedEntitlementAction()), [dispatch]);
  const getEntitlementShares = useCallback(() => dispatch(getEntitlementSharesAction(props.guestId)), [dispatch, props.guestId]);

  const [selectedTab, setSelectedTab] = useState(TABS.REDEEM);
  const [redemptionHistory, setRedemptionHistory] = useState<HistoricalRedemption[]>([]);
  const [disableClickoffAndKeypressDismissable, setDisableClickoffAndKeypressDismissable] = useState(false);

  const onDismiss = () => (props.onDismiss || doNothing)();
  const onTabChange = (newTab: TABS) => setSelectedTab(newTab);

  const TABS_DATA: Tab[] = [
    { icon: fnf, heading: getResource(resources, 'FNF_MODAL_REDEEM_TAB')},
    { icon: calendar, heading: getResource(resources, 'FNF_MODAL_HISTORY_TAB')}
  ];


  useEffect(() => { // Clear any redemptions on initial load and load entitlements
    clearRedemption();
    getFnFEntitlements();
    getEntitlementShares();
  }, [clearRedemption, getEntitlementShares, getFnFEntitlements]);

  useEffect(() => {
    if (entitlements.length && !isLoadingEntitlements) {
      setRedemptionHistory(getAllRedemptionHistory(entitlements));
    }
  }, [isLoadingEntitlements, isLoadingEntitlementShares, isRedeeming, entitlements]);


  return (
    <ModalView
      onDismiss={props.onDismiss}
      disableClickoffDismissable={disableClickoffAndKeypressDismissable && selectedTab !== TABS.SHARE_HISTORY}
      disableKeypressDismissable={disableClickoffAndKeypressDismissable && selectedTab !== TABS.SHARE_HISTORY}
      contentCss={FnFModal}
    >
      <ModalTabHeader 
        resources={resources} 
        tabs={TABS_DATA} 
        onTabChange={onTabChange} 
        onDismiss={onDismiss} 
        rootCss={css`margin-bottom: 25px;`} 
      />

      <Switcher selectedIndex={selectedTab}>

        <ModalContent css={css`min-width: 871px; margin-top: 5px;max-height: 50vh;`}>
          <FnFRedeemTab
            guest={guest}
            entitlements={entitlements}
            isLoadingEntitlements={isLoadingEntitlements}
            entitlementShares={entitlementShares || []}
            isLoadingEntitlementShares={isLoadingEntitlementShares}
            getEntitlementShares={getEntitlementShares}
            getFnFEntitlements={getFnFEntitlements}
            redemptionHistory={redemptionHistory}
            setDisableClickoffAndKeypressModal={setDisableClickoffAndKeypressDismissable}
          />
        </ModalContent>

        <ModalContent>
          <FnFHistoryTab
            entitlementShares={entitlementShares || []}
            guest={guest}
            isLoadingEntitlementShares={isLoadingEntitlementShares}
            entitlementSharesError={!!entitlementSharesError}
            redemptionHistory={redemptionHistory}
          />
        </ModalContent>
      </Switcher>
    </ModalView>
  )

}
